import React, { FC, useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { Info } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import type { GradePayGapData } from "../../../hooks";
import {
  getSalaryComponentLabel,
  getSalaryComponentTooltip,
  type SalaryComponentType,
} from "../../../../PaygapUploadData/hooks/regressionOptions";

interface GradeChartProps {
  data: GradePayGapData[];
  /** Single grading type ("I","II","III") or "all" */
  selectedGrading?: string;
  /** Single salary type or "all" */
  selectedSalaryType?: SalaryComponentType | "all";
  /** Legacy prop kept for backward compat (hidden PDF captures) */
  selectedGradings?: string[];
}

const GRADE_COLORS: Record<string, string> = {
  I: "#1976d2",
  II: "#388e3c",
  III: "#f57c00",
};

const SALARY_TYPE_COLORS: Record<string, string> = {
  net: "#1976d2",
  basic: "#388e3c",
  variable: "#f57c00",
};

const SALARY_TYPE_PATTERNS: Record<string, string> = {
  net: "",
  basic: "url(#stripe-basic)",
  variable: "url(#stripe-variable)",
};

const SUB_BAR_WIDTH = 36;
const GROUP_SPACING = 24;
const LEFT_PADDING = 60;
const RIGHT_PADDING = 40;
const TOP_PADDING = 40;
const BOTTOM_PADDING = 80;
const CHART_HEIGHT = 420;

export const GradeChart: FC<GradeChartProps> = ({
  data,
  selectedGrading = "all",
  selectedSalaryType = "all",
  selectedGradings,
}) => {
  const { t, i18n } = useTranslation();

  // Filter data by grading
  const gradingFiltered = useMemo(() => {
    // Support legacy selectedGradings prop (array)
    if (selectedGradings && !selectedGrading) {
      return data.filter((d) => selectedGradings.includes(d.gradeType));
    }

    if (selectedGrading === "all") return data;

    return data.filter((d) => d.gradeType === selectedGrading);
  }, [data, selectedGrading, selectedGradings]);

  // Determine which salary types are present
  const presentSalaryTypes = useMemo(() => {
    const types = [...new Set(gradingFiltered.map((d) => d.salaryType))];
    const order: SalaryComponentType[] = ["net", "basic", "variable"];

    return order.filter((st) => types.includes(st));
  }, [gradingFiltered]);

  // Filter by salary type
  const filteredData = useMemo(() => {
    if (selectedSalaryType === "all") return gradingFiltered;

    return gradingFiltered.filter((d) => d.salaryType === selectedSalaryType);
  }, [gradingFiltered, selectedSalaryType]);

  // Determine how many sub-bars per group
  const salaryTypesInChart = useMemo(() => {
    if (selectedSalaryType !== "all")
      return [selectedSalaryType] as SalaryComponentType[];

    return presentSalaryTypes;
  }, [selectedSalaryType, presentSalaryTypes]);
  const subBarCount = salaryTypesInChart.length;

  // Build groups: unique gradeType-gradeId combos
  const groups = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ gradeType: string; gradeId: string }> = [];

    for (const d of filteredData) {
      const key = `${d.gradeType}-${d.gradeId}`;

      if (!seen.has(key)) {
        seen.add(key);
        result.push({ gradeType: d.gradeType, gradeId: d.gradeId });
      }
    }

    return result.sort((a, b) => {
      if (a.gradeType !== b.gradeType) return a.gradeType.localeCompare(b.gradeType);

      return a.gradeId.localeCompare(b.gradeId, undefined, { numeric: true });
    });
  }, [filteredData]);

  // Chart dimensions
  const groupWidth = subBarCount * SUB_BAR_WIDTH + 4;
  const chartWidth = Math.max(
    800,
    LEFT_PADDING + groups.length * (groupWidth + GROUP_SPACING) + RIGHT_PADDING,
  );
  const contentHeight = CHART_HEIGHT - TOP_PADDING - BOTTOM_PADDING;

  const maxAbsGap = useMemo(() => {
    if (filteredData.length === 0) return 10;

    return Math.max(...filteredData.map((d) => Math.abs(d.payGap)), 10);
  }, [filteredData]);
  const maxScale = Math.ceil(maxAbsGap / 10) * 10;
  const centerY = TOP_PADDING + contentHeight / 2;

  const valueToY = (value: number): number =>
    centerY - (value / maxScale) * (contentHeight / 2);

  // Ticks
  const tickStep = maxScale <= 20 ? 5 : 10;
  const ticks: number[] = [];

  for (let i = -maxScale; i <= maxScale; i += tickStep) {
    ticks.push(i);
  }

  if (filteredData.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height={300}>
        <Typography variant="body2" color="text.secondary">
          {t("paygap.chart.noData")}
        </Typography>
      </Box>
    );
  }

  // Decide coloring strategy:
  // - If showing multiple salary types → color by salary type
  // - If showing single salary type → color by grading type
  const colorBySalary = subBarCount > 1;

  const getBarColor = (item: GradePayGapData): string => {
    if (colorBySalary) return SALARY_TYPE_COLORS[item.salaryType] || "#666";

    return GRADE_COLORS[item.gradeType] || "#666";
  };

  return (
    <>
      <Box display="flex" justifyContent="center" sx={{ overflowX: "auto" }}>
        <Box position="relative" width={chartWidth} height={CHART_HEIGHT} pb={2}>
          <svg width={chartWidth} height={CHART_HEIGHT}>
            {/* Pattern definitions for salary types */}
            <defs>
              <pattern
                id="stripe-basic"
                width="6"
                height="6"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)"
              >
                <rect
                  width="6"
                  height="6"
                  fill={SALARY_TYPE_COLORS.basic}
                  opacity={0.8}
                />
                <line x1="0" y1="0" x2="0" y2="6" stroke="#fff" strokeWidth="2" />
              </pattern>
              <pattern
                id="stripe-variable"
                width="6"
                height="6"
                patternUnits="userSpaceOnUse"
                patternTransform="rotate(-45)"
              >
                <rect
                  width="6"
                  height="6"
                  fill={SALARY_TYPE_COLORS.variable}
                  opacity={0.8}
                />
                <line x1="0" y1="0" x2="0" y2="6" stroke="#fff" strokeWidth="2" />
              </pattern>
            </defs>

            {/* Y-axis label */}
            <text
              x={15}
              y={CHART_HEIGHT / 2}
              textAnchor="middle"
              fontSize="12"
              fill="#666"
              transform={`rotate(-90, 15, ${CHART_HEIGHT / 2})`}
            >
              {t("paygap.chart.payGapAxisLabel")}
            </text>

            {/* Grid lines */}
            {ticks.map((tick) => {
              const y = valueToY(tick);

              return (
                <g key={tick}>
                  <line
                    x1={LEFT_PADDING}
                    y1={y}
                    x2={chartWidth - RIGHT_PADDING}
                    y2={y}
                    stroke={tick === 0 ? "#333" : "#e0e0e0"}
                    strokeWidth={tick === 0 ? 2 : 1}
                    strokeDasharray={tick === 0 ? undefined : "2,2"}
                  />
                  <text
                    x={LEFT_PADDING - 10}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="11"
                    fill="#666"
                  >
                    {tick}%
                  </text>
                </g>
              );
            })}

            {/* Data bars grouped */}
            {groups.map((group, groupIndex) => {
              const groupX =
                LEFT_PADDING +
                groupIndex * (groupWidth + GROUP_SPACING) +
                GROUP_SPACING / 2;

              return (
                <g key={`${group.gradeType}-${group.gradeId}`}>
                  {salaryTypesInChart.map((st, stIndex) => {
                    const item = filteredData.find(
                      (d) =>
                        d.gradeType === group.gradeType &&
                        d.gradeId === group.gradeId &&
                        d.salaryType === st,
                    );

                    if (!item) return null;

                    const x = groupX + stIndex * SUB_BAR_WIDTH + 2;
                    const payGap = item.payGap;
                    const barTopY = payGap >= 0 ? valueToY(payGap) : centerY;
                    const barBottomY = payGap >= 0 ? centerY : valueToY(payGap);
                    const barHeight = Math.abs(barBottomY - barTopY);
                    const color = getBarColor(item);
                    const pattern = colorBySalary ? SALARY_TYPE_PATTERNS[st] : "";

                    return (
                      <g key={`${item.gradeType}-${item.gradeId}-${st}`}>
                        {barHeight > 0 && (
                          <rect
                            x={x}
                            y={barTopY}
                            width={SUB_BAR_WIDTH}
                            height={barHeight}
                            fill={pattern || color}
                            opacity={pattern ? 1 : 0.8}
                            rx={2}
                            stroke={color}
                            strokeWidth={pattern ? 1 : 0}
                          />
                        )}
                        {/* Value label */}
                        <text
                          x={x + SUB_BAR_WIDTH / 2}
                          y={payGap >= 0 ? barTopY - 5 : barBottomY + 14}
                          textAnchor="middle"
                          fontSize="9"
                          fill="#333"
                          fontWeight="500"
                        >
                          {payGap.toFixed(1)}%
                        </text>
                      </g>
                    );
                  })}

                  {/* Group label on bottom */}
                  <text
                    x={groupX + groupWidth / 2}
                    y={CHART_HEIGHT - BOTTOM_PADDING + 20}
                    textAnchor="middle"
                    fontSize="11"
                    fill={GRADE_COLORS[group.gradeType] || "#333"}
                    fontWeight="bold"
                  >
                    {group.gradeType}-{group.gradeId}
                  </text>
                </g>
              );
            })}
          </svg>
        </Box>
      </Box>

      {/* Legend */}
      <Box display="flex" gap={3} mt={2} justifyContent="center" flexWrap="wrap">
        {/* Direction explanation */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ px: 2, py: 0.5, bgcolor: "grey.100", borderRadius: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            ↓ {t("paygap.chart.womenEarnLess")}
          </Typography>
          <Box sx={{ width: 1, height: 16, bgcolor: "grey.400", mx: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {t("paygap.chart.womenEarnMore")} ↑
          </Typography>
        </Box>
      </Box>

      {/* Color legend */}
      <Box display="flex" gap={3} mt={1} justifyContent="center" flexWrap="wrap">
        {colorBySalary
          ? /* Salary type legend */
            salaryTypesInChart.map((st) => {
              const label = getSalaryComponentLabel(st, i18n.language);
              const tooltip = getSalaryComponentTooltip(st, i18n.language);

              return (
                <Box key={st} display="flex" alignItems="center" gap={0.5}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      bgcolor: SALARY_TYPE_COLORS[st],
                      borderRadius: 0.5,
                      opacity: 0.8,
                    }}
                  />
                  <Typography variant="caption">{label}</Typography>
                  {tooltip && (
                    <Tooltip title={tooltip} arrow>
                      <Info sx={{ fontSize: 14, color: "action.active" }} />
                    </Tooltip>
                  )}
                </Box>
              );
            })
          : /* Grading type legend */
            [...new Set(filteredData.map((d) => d.gradeType))].sort().map((gt) => (
              <Box key={gt} display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    bgcolor: GRADE_COLORS[gt],
                    borderRadius: 0.5,
                  }}
                />
                <Typography variant="caption">
                  {t("paygap.chart.grading", "Grading")} {gt}
                </Typography>
              </Box>
            ))}
      </Box>
    </>
  );
};
